import { defineMessages, useIntl } from "react-intl";

import { Substyle } from "common/core/typography";

import { ChartsSection } from "./shared/charts_section";
import { EmptyChart } from "./shared/empty_chart";
import { NumericalChart } from "./shared/numerical_chart";
import type { NotarizeSummaryScalarHash } from "./shared/utils";

const MESSAGES = defineMessages({
  meetingFeedback: {
    id: "ca91f754-68c6-43bd-aac6-e56777726898",
    defaultMessage: "Meeting and recipient feedback",
  },
  medianNotaryMeetingTime: {
    id: "f1c2b3d4-5e6f-4a7b-8c9d-e0f1a2b3c4d5",
    defaultMessage: "Median notary meeting time",
  },
  minutes: {
    id: "2bc85deb-93bf-4309-9b8f-6251e2da25ed",
    defaultMessage: "minutes",
  },
  averageNotaryRating: {
    id: "0e7d1e70-b3a3-49c9-a4a5-c236a298f904",
    defaultMessage: "Average notary rating",
  },
  averageNotaryRatingDescription: {
    id: "8585566a-1edf-413b-95df-5592a8fdaf5a",
    defaultMessage: "Based on customer post-meeting review",
  },
  emptyMeetingHeading: {
    id: "10b81ecc-2d23-47db-9fb8-eb1e9ea55ad5",
    defaultMessage: "No meetings in the last 30 days",
  },
  emptyFeedbackHeading: {
    id: "d2f3e4a5-6b7c-4d8e-9f0a-1a2b3c4d5e6f",
    defaultMessage: "No feedback in the last 30 days",
  },
  emptyMeetingTimeChartDescription: {
    id: "72dfbe8e-76c4-459d-a42b-15d8b5f5b895",
    defaultMessage: "Median notary meeting time will display here.",
  },
  emptyRatingChartDescription: {
    id: "b1f0c8d2-3e4a-4c5f-9b6d-7e8f5c1b2c3d",
    defaultMessage: "Median notary rating will display here.",
  },
});

export function NotaryMeetingFeedbackSection({
  notaryMeetingTime,
  notaryRating,
  last30Days,
}: {
  notaryMeetingTime: NotarizeSummaryScalarHash[];
  notaryRating: NotarizeSummaryScalarHash[];
  last30Days: string;
}) {
  const intl = useIntl();

  const notaryMeetingTimeValue = notaryMeetingTime[0]?.median;
  const notaryRatingValue = notaryRating[0]?.average;
  return (
    <ChartsSection
      heading={intl.formatMessage(MESSAGES.meetingFeedback)}
      description={last30Days}
      firstChart={
        <>
          {!notaryMeetingTimeValue ? (
            <EmptyChart
              heading={intl.formatMessage(MESSAGES.emptyMeetingHeading)}
              description={intl.formatMessage(MESSAGES.emptyMeetingTimeChartDescription)}
            />
          ) : (
            <NumericalChart
              number={Number(notaryMeetingTimeValue).toFixed(2)}
              detail={intl.formatMessage(MESSAGES.medianNotaryMeetingTime)}
              figureLabel={intl.formatMessage(MESSAGES.minutes)}
              figureLabelToBaseline
            />
          )}
        </>
      }
      secondChart={
        <>
          {!notaryRatingValue ? (
            <EmptyChart
              heading={intl.formatMessage(MESSAGES.emptyFeedbackHeading)}
              description={intl.formatMessage(MESSAGES.emptyRatingChartDescription)}
            />
          ) : (
            <NumericalChart
              number={Number(notaryRatingValue).toFixed(2)}
              detail={intl.formatMessage(MESSAGES.averageNotaryRating)}
              figureLabel="/5"
              figureLabelToBaseline
              subDetail={
                <Substyle textColor="subtle">
                  {intl.formatMessage(MESSAGES.averageNotaryRatingDescription)}
                </Substyle>
              }
            />
          )}
        </>
      }
    />
  );
}
